import React, { useState } from 'react';
import { Pane, Heading, Paragraph, WarningSignIcon, RefreshIcon, ChevronDownIcon, ChevronUpIcon, majorScale } from 'evergreen-ui';
import Btn from '../btn/btn';

function ErrorBoundaryFallback({ msg }) {
  const [showDetails, setShowDetails] = useState(false);

  const handleReload = () => {
    window.location.reload();
  };

  return (
    <Pane
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      padding={majorScale(4)}
      marginX="auto"
      marginY={majorScale(6)}
      maxWidth={480}
      width="90%"
      background="#FFFFFF"
      borderRadius={12}
      boxShadow="0px 8px 30px rgba(0, 0, 0, 0.08)"
      border="1px solid rgba(0, 0, 0, 0.08)"
      textAlign="center"
    >
      <style>
        {`
          .error-fallback-btn {
            background-color: #e25200 !important;
            border-color: #e25200 !important;
            color: #ffffff !important;
          }
          .error-fallback-btn:hover {
            background-color: #c2410c !important;
            border-color: #c2410c !important;
            color: #ffffff !important;
          }
          .error-details-btn {
            color: #6B7280 !important;
            background-color: transparent !important;
            border-color: transparent !important;
          }
          .error-details-btn:hover {
            color: #1F2937 !important;
            background-color: transparent !important;
            border-color: transparent !important;
          }
        `}
      </style>

      {/* Icon with soft yellow-orange warning background badge */}
      <Pane
        display="flex"
        alignItems="center"
        justifyContent="center"
        width={64}
        height={64}
        borderRadius="50%"
        backgroundColor="#FEF3C7" // Soft warning yellow
        color="#D97706" // Warm orange/yellow for warning
        marginBottom={majorScale(3)}
      >
        <WarningSignIcon size={32} color="currentColor" />
      </Pane>

      <Heading
        size={700}
        marginBottom={majorScale(2)}
        fontWeight={700}
        color="#1F2937"
        fontFamily="ProximaNova, sans-serif"
      >
        Oops! Something went wrong
      </Heading>

      <Paragraph
        size={400}
        color="#4B5563"
        marginBottom={majorScale(4)}
        lineHeight={1.6}
        fontFamily="ProximaNova, sans-serif"
      >
        We encountered an unexpected error while loading this page. Please try refreshing. If the issue persists, feel free to contact our support team.
      </Paragraph>

      <Pane
        display="flex"
        flexDirection="column"
        gap={majorScale(2)}
        width="100%"
      >
        {/* Refresh button using custom Btn component */}
        <Btn
          className="error-fallback-btn"
          look="primary-filled"
          onClick={handleReload}
          width="100%"
          height={40}
          borderRadius={8}
          display="flex"
          alignItems="center"
          justifyContent="center"
          fontSize="1rem"
          fontWeight={600}
        >
          <RefreshIcon marginRight={majorScale(1)} />
          Refresh Page
        </Btn>

        {/* Toggle Details button using tertiary-minimal look */}
        {msg && (
          <Btn
            className="error-details-btn"
            look="tertiary-minimal"
            onClick={() => setShowDetails(!showDetails)}
            width="100%"
            height={36}
            borderRadius={8}
            fontSize="0.875rem"
            color="#6B7280"
          >
            {showDetails ? (
              <>
                Hide Technical Details
                <ChevronUpIcon marginLeft={majorScale(1)} />
              </>
            ) : (
              <>
                Show Technical Details
                <ChevronDownIcon marginLeft={majorScale(1)} />
              </>
            )}
          </Btn>
        )}
      </Pane>

      {/* Technical Details panel */}
      {showDetails && msg && (
        <Pane
          width="100%"
          marginTop={majorScale(3)}
          padding={majorScale(2)}
          backgroundColor="#F9FAFB"
          borderRadius={8}
          border="1px solid #E5E7EB"
          textAlign="left"
        >
          <Heading
            size={300}
            color="#374151"
            marginBottom={majorScale(1)}
            fontWeight={600}
            fontFamily="ProximaNova, sans-serif"
          >
            Error Message:
          </Heading>
          <Paragraph
            fontFamily="monospace"
            fontSize="0.8rem"
            color="#DC2626"
            style={{ wordBreak: 'break-all', whiteSpace: 'pre-wrap' }}
          >
            {msg}
          </Paragraph>
        </Pane>
      )}
    </Pane>
  );
}

export default ErrorBoundaryFallback;
